import React, { useState } from 'react';
import clsx from 'clsx';
import { caretRight } from '../../../../../../assets/hostedassets';
import { isEmpty } from '../../../../../../utils/utils';
import { AuditedRowSpec, AUDITED_UNIT_SHORT_LABEL, CLASSIFICATION_COLUMN_LABEL } from '../../auditedStatementLayout';
import AuditedStatementTable, { AuditedStatementHeaderClone } from '../AuditedStatementTable/AuditedStatementTable';
import { useAuditedStatementSectionLogic } from './useAuditedStatementSectionLogic';
import FinancialsIngestionNote from '../../../Financials/financialsIngestion/FinancialsIngestionNote';
import { SourceFile, SourceFilesStatus, SourceStatement } from '../../../../../../common/SourceFileTag/types';
import styles from './AuditedStatementSection.module.scss';

type SectionTableHeader = { name: string; key: string; sourceFiles?: SourceFile[] };

interface AuditedStatementSectionProps {
    title: string;
    layout: AuditedRowSpec[];
    tableData: { headers?: SectionTableHeader[]; rows?: any[]; sourceFilesStatus?: SourceFilesStatus };
    loading: boolean;
    /** Display unit for all figures ('Lakh' | 'Crore'), forwarded to the table for rescaling. */
    unit: string;
    defaultExpanded?: boolean;
    /** RECUR-28096 — a financials file for this section is still being extracted. */
    inProgress?: boolean;
    /** Provisional's opt-in "Reference Documents" row, forwarded straight through to
     * `AuditedStatementTable`. No separate forward to `AuditedStatementHeaderClone` needed: the
     * row lives in `<tbody>`, and the clone only ever clones `<thead>` (see
     * AuditedStatementTable's own `referenceDocumentsRow` docblock). Audited never passes this. */
    referenceDocumentsRow?: {
        statement: SourceStatement;
        onOpen: (docId: string, fileName: string) => void;
    };
    /** Forwarded verbatim to BOTH AuditedStatementTable's real thead AND the pinned
     * AuditedStatementHeaderClone below, so the two stay pixel-identical while stuck. Defaults to
     * CLASSIFICATION_COLUMN_LABEL; Provisional overrides to 'Financial Year'. */
    firstColumnLabel?: string;
}

/**
 * Collapsible section for ONE audited-financials statement (Balance Sheet / P&L / Cash Flow),
 * built to the RECUR-27010 redesign mock. Renders the presentational `AuditedStatementTable`,
 * fully expanded with no per-row collapse — there is no drill-down/RawTable/Edit-Classification
 * affordance on this surface either. The section header owns a sticky band (RECUR-27656): once the
 * section scrolls past the top of the scroller, a scroll-synced clone of the table's year-column
 * header row pins above the content so the classification/year headers stay visible. Provisional
 * Financials also renders through this component — `referenceDocumentsRow` is its one opt-in,
 * forwarded to the table only (see that prop's own docblock); Audited never passes it.
 */
const AuditedStatementSection = ({
    title,
    layout,
    tableData,
    loading,
    unit,
    defaultExpanded = true,
    inProgress = false,
    referenceDocumentsRow,
    firstColumnLabel = CLASSIFICATION_COLUMN_LABEL,
}: AuditedStatementSectionProps) => {
    const [isExpanded, setIsExpanded] = useState<boolean>(defaultExpanded);

    // UAT RECUR-26713 (Nishank #5): an empty statement collapses and shows an inline
    // "(No data available)" next to a muted heading — not a verbose message row below an
    // expanded heading. `hasData` mirrors AuditedStatementTable's own data check.
    const valueHeaders = (tableData?.headers ?? []).filter((header) => header.name !== 'Classification');
    const rows = (tableData?.rows ?? []).filter((row) => !isEmpty(row));
    const hasData = valueHeaders.length > 0 && rows.length > 0;
    const expanded = isExpanded && hasData;

    const { stuck, headerWidths, setHeaderWidths, sentinelRef, cloneViewportRef, handleHorizontalScroll } =
        useAuditedStatementSectionLogic();

    return (
        <div className={styles.Section}>
            <div className={styles.StickySentinel} aria-hidden="true" ref={sentinelRef} />
            <div className={styles.StickyBand}>
                <div className={clsx(styles.HeaderRow, { [styles.HeaderRowCollapsed]: !expanded })}>
                    <button
                        type="button"
                        className={styles.SectionHeader}
                        onClick={() => hasData && setIsExpanded((prev) => !prev)}
                        disabled={!hasData}
                        data-testid="statement-section-header"
                    >
                        <img src={caretRight} alt="" className={clsx(styles.Chevron, { [styles.ChevronExpanded]: expanded })} />
                        <span
                            className={clsx(styles.SectionTitle, { [styles.SectionTitleMuted]: !hasData })}
                            data-testid="statement-section-title"
                        >
                            {title}
                            {AUDITED_UNIT_SHORT_LABEL[unit] && (
                                <span className={styles.SectionUnit}> (All values in {AUDITED_UNIT_SHORT_LABEL[unit]})</span>
                            )}
                        </span>
                        {!hasData && !inProgress && <span className={styles.NoData}>(No data available)</span>}
                        {inProgress && <FinancialsIngestionNote />}
                    </button>
                </div>
                {stuck && expanded && headerWidths.length > 0 && (
                    // Square white backdrop behind the rounded clone — without it the corner
                    // notches outside the border-radius are transparent and scrolling rows
                    // peek through them.
                    <div className={styles.CloneBackdrop} aria-hidden="true">
                        <div className={styles.HeaderCloneViewport} ref={cloneViewportRef} data-testid="statement-header-clone">
                            <AuditedStatementHeaderClone
                                valueHeaders={valueHeaders}
                                widths={headerWidths}
                                firstColumnLabel={firstColumnLabel}
                            />
                        </div>
                    </div>
                )}
            </div>

            {expanded && (
                <div className={styles.SectionBody}>
                    <AuditedStatementTable
                        layout={layout}
                        tableData={tableData}
                        loading={loading}
                        unit={unit}
                        emptyMessage=""
                        onHeaderCellWidthsChange={setHeaderWidths}
                        onHorizontalScroll={handleHorizontalScroll}
                        hideHeader={stuck && expanded}
                        referenceDocumentsRow={referenceDocumentsRow}
                        firstColumnLabel={firstColumnLabel}
                    />
                </div>
            )}
        </div>
    );
};

export default AuditedStatementSection;
